import moment from 'moment';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { localeStore } from '~/mobx/localesStore';
import { modalStore } from '~/mobx/modalStore';
import { IWorkingHoursModalParams } from '~/mobx/modalStore/types';
import AddButton from '~/view/components/Controls/AddButton/AddButton';
import SlotCard from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/components/TimeSlots/SlotCard/SlotCard';
import { WORKING_HOURS_ORDER_TYPE } from '~/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';
import { TBranchWorkingTime } from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/types';
import { getNextDayOfWeek } from '~/helpers/getNextDayOfWeek/getNextDayOfWeek';
import './style.scss';
import integrationsStore from '~/mobx/integrationStore';
import IntegrationManager from '~/view/components/IntegrationManager/IntegrationManager';
import { RESTRICTION_ACCESS } from '~/constants/integrations';

type Props = {
  day: string;
  workingTime: TBranchWorkingTime[];
  disabled: boolean;
  posIntAccess: RESTRICTION_ACCESS | undefined;
  onSave: (
    weekday: string,
    id: number | undefined,
    startTime: Date,
    closeTime: Date,
    menuGroupId: number | undefined,
    menuGroupName: string | undefined,
    orderType: WORKING_HOURS_ORDER_TYPE | undefined
  ) => void;
  onDelete: (id: number | undefined) => void;
  withoutMenuGroup?: boolean;
  withoutOrderType?: boolean;
};

const TimeSlots = observer(({ day, workingTime, disabled, posIntAccess, onSave, onDelete, withoutMenuGroup, withoutOrderType }: Props) => {
  const slots = workingTime.filter((slot) => slot.weekday === day);

  const nextDaySlots = useMemo(() => {
    const nextDay = getNextDayOfWeek(day);
    return workingTime.filter((slot) => slot.weekday === nextDay);
  }, [workingTime.length]);

  const sortedSlots = useMemo(
    () => slots.sort((left, right) => moment(left.closeTime, 'HH:mm:ss').diff(moment(right.closeTime, 'HH:mm:ss'))),
    [slots]
  );

  const openTimeSlotEditing = (params?: Partial<IWorkingHoursModalParams>) => {
    modalStore.openWorkingHoursModal({
      weekday: day,
      validationSlotsHours: [...slots, ...nextDaySlots],
      posIntRestriction: {
        access: posIntAccess,
        shouldDisable: disabled,
      },
      onSave,
      onDelete,
      withoutMenuGroup,
      withoutOrderType,
      ...params,
    });
  };

  return (
    <div className="day-column">
      <h3 className="family-regular text-size-18 letter-spacing-12 text-uppercase">{localeStore.t(day)}</h3>

      <div className="d-flex flex-column gap-5 mt-4">
        {sortedSlots.map((slot) => (
          <SlotCard
            key={slot.id}
            id={slot.id}
            startTime={slot.startTime}
            closeTime={slot.closeTime}
            orderType={slot.orderType}
            menuGroupId={slot.menuGroupId}
            menuGroupName={slot.menuGroupName}
            onClick={openTimeSlotEditing}
            withoutMenuGroup={withoutMenuGroup}
            withoutOrderType={withoutOrderType}
          />
        ))}
      </div>

      <IntegrationManager setting={integrationsStore.posRestrictions?.branchWorkingHours} withTooltip tooltipOptions={{ show: disabled }}>
        <AddButton title="branch_working_hour_add_new_timeslot_button" className={slots.length ? 'mt-4' : ''} onAdd={openTimeSlotEditing} />
      </IntegrationManager>
    </div>
  );
});

export default TimeSlots;
